import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  useAppRegion,
  useCurrentUser,
  useEnabledFeatures,
} from '@/hooks/dataHelpers/useCurrentUser';
import { displaySettingsAllowed } from '@/lib/displaySettingsAccess';
import { useSwitchIdentity } from '@/lib/hooks/useSwitchIdentity';
import { getAppType, getAppVersionWithRegion } from '@/lib/offline/OfflineAppHelper';
import { selectPartnerName, useCommonStore } from '@/store/commonStore';
import {
  AccessibilityLinear,
  InfoCircleLinear,
  LogoutLinear,
  UserCircleLinear,
} from '@assessprep/icons/solar';
import { useCustom, useLogout, useWarnAboutChange } from '@refinedev/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { DisplaySettingsDialog } from './DisplaySettingsDialog';

/**
 * APL-11850 — v4 user menu (avatar pill in the sidebar footer, opening
 * upward — matches v3's bottom-left SiderFooter IA). BB-feedback4 ports the
 * remaining v3 SiderFooter sections: Accounts (org_user switching) and
 * SERVICES (ManageBac-portal links + demo placeholders). The v3 support
 * cluster (whats-new, help-center, book-training, report-an-issue) moved OUT
 * of this menu to the sidebar footer's SidebarSupport (dev direction 03 Aug);
 * only the app-version row (native apps, staging/dev) stays here. The pill
 * itself is two-line: name + a muted "{Role} · {School}" meta line sourced
 * from the same org_users entry the Accounts section renders. Language
 * deliberately stays in the Display settings modal (v4 decision) — opened
 * from the Display item below View profile since the accessibility-modal
 * rework (student-gated on the accessibility_settings feature); logout
 * mirrors the v1 sider behavior including the unsaved-changes confirm.
 */

interface OrgUserEntry {
  uuid: string;
  role?: string;
  archived?: boolean;
  org?: {
    uuid?: string;
    name?: string;
    org_type?: string;
    attachments_json?: { top_nav_logo?: string };
  };
}

const upperFirst = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '');

/** v3 sortOrgUsers parity — districts first, each bucket A→Z by org name. */
const sortOrgUsers = (orgUsers: OrgUserEntry[]): OrgUserEntry[] => {
  const orgName = (entry: OrgUserEntry) => (entry.org?.name ?? '').trim().toLowerCase();
  const byName = (a: OrgUserEntry, b: OrgUserEntry) => orgName(a).localeCompare(orgName(b));
  const isDistrict = (entry: OrgUserEntry) => entry.org?.org_type === 'district';
  return [
    ...orgUsers.filter(isDistrict).sort(byName),
    ...orgUsers.filter((e) => !isDistrict(e)).sort(byName),
  ];
};

/** Radio-look active indicator (v3 renders an antd Radio on the active row). */
const ActiveDot = ({ active }: { active: boolean }) => (
  <span
    aria-hidden="true"
    className={`ms-auto flex size-4 shrink-0 items-center justify-center rounded-full border ${
      active ? 'border-primary' : 'border-muted-foreground/40'
    }`}
  >
    {active && <span className="size-2 rounded-full bg-primary" />}
  </span>
);

const RowMeta = ({
  logo,
  title,
  subtitle,
}: {
  logo?: string;
  title: string;
  subtitle?: string;
}) => (
  <span className="flex min-w-0 flex-1 items-center gap-2">
    <Avatar className="size-7 shrink-0 rounded-md">
      {logo ? <AvatarImage src={logo} alt="" /> : null}
      <AvatarFallback className="rounded-md text-xs">
        {title.charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
    <span className="flex min-w-0 flex-col">
      <span className="truncate text-sm">{title}</span>
      {subtitle ? <span className="truncate text-xs text-muted-foreground">{subtitle}</span> : null}
    </span>
  </span>
);

export const UserMenu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const appRegion = useAppRegion();
  const enabledFeatures = useEnabledFeatures();
  const appType = getAppType();
  const partnerName = useCommonStore(selectPartnerName);
  const { mutate: mutateLogout, isPending: isLogoutLoading } = useLogout();
  const { warnWhen, setWarnWhen } = useWarnAboutChange();
  const { switchOrgUser, pendingId } = useSwitchIdentity();
  const [displaySettingsOpen, setDisplaySettingsOpen] = useState(false);

  const isDemo = import.meta.env.VITE_MODE === 'demo';

  // v3 SiderFooter wire: portal-linked services only exist for MB-linked
  // global-region accounts.
  const shouldFetchMbData = !!(
    currentUser?.org?.is_mb &&
    currentUser?.custom_fields?.mb_account_uid &&
    appRegion === 'global'
  );
  const { data: mbAccountsData } = useCustom({
    url: '/get_mb_accounts_portal_linked_data',
    method: 'get',
    queryOptions: { enabled: shouldFetchMbData },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mbApplications = ((mbAccountsData?.data as any)?.applications ?? []) as Array<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mbUsers = ((mbAccountsData?.data as any)?.users ?? []) as Array<any>;
  const showServices = isDemo || mbApplications.length > 0 || mbUsers.length > 0;

  // v3 menuItemRender visibility: students see only their current org; archived
  // org_users hidden; non-current OSC org_users hidden.
  const activeOrgUuid = currentUser?.org?.uuid;
  const orgUsers = sortOrgUsers((currentUser?.org_users ?? []) as OrgUserEntry[]).filter(
    (orgUser) => {
      const isCurrent = orgUser.org?.uuid === activeOrgUuid;
      if (currentUser?.role === 'student' && !isCurrent) return false;
      if (orgUser.archived) return false;
      if (!isCurrent && orgUser.org?.uuid?.includes('osc')) return false;
      return true;
    }
  );

  // Support cluster lives in the sidebar footer (SidebarSupport) now — only
  // the app-version row (native apps, staging/dev builds) remains here.
  const showAppVersion =
    appType !== 'web' &&
    (import.meta.env.VITE_MODE === 'staging' || import.meta.env.VITE_MODE === 'development');

  // Accessibility-modal rework: staff always; students/parents need the org's
  // accessibility_settings feature; unloaded user → hidden (no pre-load flash).
  const showDisplaySettings = displaySettingsAllowed(currentUser?.role, enabledFeatures);

  // v3 profile-item gate, verbatim.
  const showProfile =
    currentUser?.role !== 'qb_author' &&
    currentUser?.role !== 'support' &&
    appType === 'web' &&
    partnerName !== '21k' &&
    currentUser?.org?.org_type !== 'district';

  const displayName = currentUser?.name || currentUser?.email || '';
  // Pill meta line — role + current school. School name comes from the same
  // org_users entry the Accounts section renders (currentUser.org carries no
  // name on the identity wire); role reuses the RoleMappingTable.role_* keys
  // (all seven backend roles mapped) with a capitalized fallback as the net.
  const currentOrgName =
    ((currentUser?.org_users ?? []) as OrgUserEntry[]).find(
      (orgUser) => orgUser.org?.uuid === activeOrgUuid
    )?.org?.name ??
    currentUser?.org?.name ??
    '';
  const roleLabel = currentUser?.role
    ? t(`RoleMappingTable.role_${currentUser.role}`, { defaultValue: upperFirst(currentUser.role) })
    : '';
  const pillMeta =
    roleLabel && currentOrgName
      ? t('V4Shell.user_pill_meta', { role: roleLabel, school: currentOrgName })
      : roleLabel || currentOrgName;
  const initials =
    displayName
      .split(' ')
      .map((part: string) => part.charAt(0))
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';

  const handleLogout = () => {
    if (warnWhen) {
      const confirmed = window.confirm(
        t('warnWhenUnsavedChanges', 'Are you sure you want to leave? You have unsaved changes.')
      );
      if (confirmed) {
        setWarnWhen(false);
        mutateLogout();
      }
    } else {
      mutateLogout();
    }
  };

  return (
    <>
      {displaySettingsOpen && (
        <DisplaySettingsDialog open={displaySettingsOpen} onOpenChange={setDisplaySettingsOpen} />
      )}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          {/* p-1 in icon-collapsed mode so the size-6 avatar fits the size-8 rail button
              (collapsed rail is unchanged — the meta line only exists expanded).
              h-auto min-h-12 (not lg's fixed h-12) so the two-line stack isn't
              clipped under ap-line-relaxed/loose; min-h-0 when collapsed so the
              size-8! rail override still governs (min-height beats height). */}
          <SidebarMenuButton
            size="lg"
            tooltip={displayName}
            aria-label={t('V4Shell.user_menu')}
            data-testid="v4-user-menu"
            className="h-auto min-h-12 group-data-[collapsible=icon]:min-h-0 group-data-[collapsible=icon]:p-1!"
          >
            <Avatar className="size-6 shrink-0">
              <AvatarFallback className="text-xs">{initials}</AvatarFallback>
            </Avatar>
            <span className="flex min-w-0 flex-1 flex-col text-start">
              <span className="truncate">{displayName}</span>
              {pillMeta ? (
                <span
                  className="truncate text-xs font-normal text-muted-foreground"
                  title={pillMeta}
                  data-testid="sidebar-user-pill-meta"
                >
                  {pillMeta}
                </span>
              ) : null}
            </span>
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start" className="w-72">
          <DropdownMenuLabel>
            <span className="block truncate">{displayName}</span>
            {currentUser?.email && currentUser?.name && (
              <span className="block truncate text-xs font-normal text-muted-foreground">
                {currentUser.email}
              </span>
            )}
          </DropdownMenuLabel>
          {orgUsers.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuGroup data-testid="v4-user-menu-accounts">
                <DropdownMenuLabel className="text-xs font-medium text-muted-foreground uppercase">
                  {t('CommonText.accounts')}
                </DropdownMenuLabel>
                {orgUsers.map((orgUser) => {
                  const isCurrent = orgUser.org?.uuid === activeOrgUuid;
                  const isDistrict = orgUser.org?.org_type === 'district';
                  const isPending = pendingId === orgUser.uuid;
                  return (
                    <DropdownMenuItem
                      key={orgUser.uuid}
                      disabled={pendingId !== null}
                      onSelect={(event) => {
                        // Keep the menu open on the active row (v3 early-returns).
                        if (isCurrent) {
                          event.preventDefault();
                          return;
                        }
                        switchOrgUser({ uuid: orgUser.uuid });
                      }}
                      data-testid={`v4-user-menu-org-user-${orgUser.uuid}`}
                    >
                      <RowMeta
                        logo={orgUser.org?.attachments_json?.top_nav_logo}
                        title={`${isDistrict ? '(D) ' : ''}${orgUser.org?.name ?? ''}`}
                        subtitle={upperFirst(orgUser.role ?? '')}
                      />
                      {isPending ? (
                        <Spinner aria-hidden="true" className="ms-auto size-4 shrink-0" />
                      ) : (
                        <ActiveDot active={isCurrent} />
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuGroup>
            </>
          )}
          {showServices && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuGroup data-testid="v4-user-menu-services">
                <DropdownMenuLabel className="text-xs font-medium text-muted-foreground uppercase">
                  {t('SiderFooter.services')}
                </DropdownMenuLabel>
                {mbApplications
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  .filter((app: any) => app.partner_id !== 'assess_prep')
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  .map((app: any) => (
                    <DropdownMenuItem
                      key={app.partner_id}
                      onSelect={() => window.open(app.jump_url)}
                      data-testid={`v4-user-menu-service-${app.partner_id}`}
                    >
                      <RowMeta
                        logo={app.logo}
                        title={app.name ?? ''}
                        subtitle={upperFirst(app.description ?? '')}
                      />
                    </DropdownMenuItem>
                  ))}
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {mbUsers.map((mbUser: any, index: number) => (
                  <DropdownMenuItem
                    key={`mb-user-${index}`}
                    onSelect={() => window.open(mbUser.jump_url)}
                    data-testid={`v4-user-menu-service-user-${index}`}
                  >
                    <RowMeta
                      logo={mbUser.logo}
                      title={mbUser.school_name ?? ''}
                      subtitle={upperFirst(mbUser.role ?? '')}
                    />
                  </DropdownMenuItem>
                ))}
                {isDemo && (
                  <>
                    {[
                      {
                        key: 'managebac',
                        logo: 'https://accounts-partners.devel.faria.org/assets/logos/managebac-e1597aef64c4b5455c222237a2b815417cf571d41a124c1dccdecd08d59498a6.svg',
                        title: t('SiderFooter.managebac'),
                        subtitle: t('SiderFooter.curriculum_first_learning_platform'),
                      },
                      {
                        key: 'smartprep',
                        logo: 'https://accounts-partners.devel.faria.org/assets/logos/smartprep-99983f799e611deae2593340a2497da400f16dd641bdb22f8ae6a17ee5f418c8.svg',
                        title: t('SiderFooter.osc_study'),
                        subtitle: t('SiderFooter.all_in_one_ib_dp_revision'),
                      },
                    ].map((service) => (
                      <Tooltip key={service.key}>
                        <TooltipTrigger asChild>
                          {/* Styled like a disabled item but NOT Radix-disabled —
                            pointer-events must stay on for the tooltip. */}
                          <div
                            aria-disabled="true"
                            className="flex cursor-not-allowed items-center gap-2 rounded-sm px-2 py-1.5 opacity-60"
                            data-testid={`v4-user-menu-service-${service.key}`}
                          >
                            <RowMeta
                              logo={service.logo}
                              title={service.title}
                              subtitle={service.subtitle}
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="left">
                          {t('SiderFooter.disabled_for_demo')}
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </>
                )}
              </DropdownMenuGroup>
            </>
          )}
          {showAppVersion && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuGroup data-testid="v4-user-menu-support">
                <DropdownMenuItem disabled data-testid="v4-user-menu-app-version">
                  <InfoCircleLinear aria-hidden className="me-2 size-4" />
                  {`${t('StudentInfoModal.app_version')}: v${getAppVersionWithRegion(appType)}`}
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </>
          )}
          <DropdownMenuSeparator />
          {showProfile && (
            <DropdownMenuItem
              onClick={() => navigate('/profile')}
              data-testid="v4-user-menu-profile"
            >
              <UserCircleLinear aria-hidden className="me-2 size-4" />
              {t('SiderFooter.view_profile')}
            </DropdownMenuItem>
          )}
          {showDisplaySettings && (
            <DropdownMenuItem
              onSelect={() => setDisplaySettingsOpen(true)}
              data-testid="display-settings-menu-item"
            >
              <AccessibilityLinear aria-hidden className="me-2 size-4" />
              {t('V4Shell.display_settings')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={handleLogout}
            disabled={isLogoutLoading}
            data-testid="v4-user-menu-logout"
          >
            <LogoutLinear aria-hidden className="me-2 size-4" />
            {isLogoutLoading ? t('CommonText.logging_out') : t('CommonText.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
